import { C, st } from '../theme'
import { useState, useEffect, useRef } from 'react'
import { useAppPrefs } from '../AppPrefs'

export const PAGE_SIZE = 15

export function Pagination({ page, total, onChange }) {
  if (total <= 1) return null
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
      <button style={{ ...st.btn('default'), padding: '6px 14px' }} onClick={() => onChange(page - 1)} disabled={page === 1}>‹ 上一頁</button>
      <span style={{ fontSize: 12, color: C.muted }}>第 {page} / {total} 頁</span>
      <button style={{ ...st.btn('default'), padding: '6px 14px' }} onClick={() => onChange(page + 1)} disabled={page === total}>下一頁 ›</button>
    </div>
  )
}

export function Modal({ title, onClose, children, wide }) {
  const mob = window.innerWidth <= 768
  const sheetRef = useRef(null)

  // 鍵盤彈出時動態調整 paddingBottom，讓按鈕不被鍵盤蓋住
  useEffect(() => {
    if (!mob) return
    const vv = window.visualViewport
    if (!vv) return
    const update = () => {
      if (!sheetRef.current) return
      const kbHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      sheetRef.current.style.paddingBottom = kbHeight > 0
        ? `${kbHeight + 16}px`
        : 'max(16px, env(safe-area-inset-bottom, 0px))'
    }
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    update()
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [mob])

  // 防止背景捲動
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 200, display: 'flex', alignItems: mob ? 'flex-end' : 'center', justifyContent: 'center', padding: mob ? 0 : 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={sheetRef}
        style={{
          ...st.card,
          width: '100%',
          maxWidth: mob ? '100%' : (wide ? 760 : 520),
          borderColor: '#2a5aa0',
          // dvh = dynamic viewport height (縮小時排除鍵盤)，舊版瀏覽器 fallback 到 vh
          maxHeight: mob ? 'min(92dvh, 92vh)' : '88vh',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch', // iOS 慣性滑動
          overscrollBehavior: 'contain',    // 防止捲動穿透到底層
          borderRadius: mob ? '20px 20px 0 0' : 16,
        }}
      >
        {mob && <div style={{ width: 36, height: 4, background: '#d1d5db', borderRadius: 2, margin: '0 auto 14px', display: 'block' }} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{title}</div>
          <button onClick={onClose} style={{ width: 44, height: 44, borderRadius: '50%', background: '#f1f5f9', border: 'none', color: C.muted, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

export function StatCard({ icon, value, label, en, color, warn }) {
  return (
    <div style={{ ...st.card, borderColor: warn && value > 0 ? C.redBorder : C.border }}>
      <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: warn && value > 0 ? C.red : (color || C.accent), marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</div>
      {en && <div style={{ fontSize: 10, color: C.muted, opacity: 0.6, marginTop: 2 }}>{en}</div>}
    </div>
  )
}

export function ScrollTable({ children }) {
  return <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>{children}</table></div>
}

export function PageHeader({ title, en, vi, subtitle }) {
  const { lang } = useAppPrefs()
  const mainTitle = lang === 'en' ? (en || title) : lang === 'vi' ? (vi || en || title) : title
  const subTitle = lang === 'zh-TW' ? en : lang === 'en' ? title : (en || title)
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 26, fontWeight: 900, color: C.text }}>{mainTitle}</div>
        {subTitle && subTitle !== mainTitle && <div style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>{subTitle}</div>}
      </div>
      {subtitle && <div style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>{subtitle}</div>}
    </div>
  )
}

// Bilingual Button
export function BiBtn({ zh, en, variant = 'primary', onClick, disabled, style = {} }) {
  const { st } = require('../theme') // inline to avoid circular
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...st.btn(variant), display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2, ...style }}
    >
      <span style={{ fontSize: 13 }}>{zh}</span>
      {en && <span style={{ fontSize: 9, opacity: 0.55, marginTop: 1 }}>{en}</span>}
    </button>
  )
}

// Bilingual inline label helper (use inside JSX)
export function BiLabel({ zh, en }) {
  return (
    <span>
      {zh}
      {en && <span style={{ fontSize: 9, opacity: 0.45, marginLeft: 4 }}>{en}</span>}
    </span>
  )
}

export function EmptyState({ icon = '📭', text = '尚無資料' }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: 13 }}>{text}</div>
    </div>
  )
}

// ── Searchable Select ──
export function SearchSelect({ options, value, onChange, placeholder = '搜尋或選擇...', displayKey = 'label', valueKey = 'value' }) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const filtered = options.filter(o => {
    const label = typeof o === 'string' ? o : o[displayKey] || ''
    return label.toLowerCase().includes(search.toLowerCase())
  })

  const selectedOption = options.find(o => {
    const val = typeof o === 'string' ? o : o[valueKey]
    return val === value
  })

  const selectedLabel = selectedOption
    ? (typeof selectedOption === 'string' ? selectedOption : selectedOption[displayKey])
    : ''

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={{ ...st.input, cursor: 'pointer' }}
        value={open ? search : selectedLabel}
        placeholder={placeholder}
        onChange={e => { setSearch(e.target.value); setOpen(true) }}
        onFocus={() => { setOpen(true); setSearch('') }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, zIndex: 999, maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', marginTop: 4 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 16px', fontSize: 13, color: C.muted }}>找不到符合的選項</div>
          ) : filtered.map((o, i) => {
            const val = typeof o === 'string' ? o : o[valueKey]
            const label = typeof o === 'string' ? o : o[displayKey]
            const sub = typeof o === 'object' && o.sub ? o.sub : null
            return (
              <div key={i} onMouseDown={() => { onChange(val); setSearch(''); setOpen(false) }}
                style={{ padding: '11px 16px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: val === value ? C.accentDim : 'transparent', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = C.accentDim}
                onMouseLeave={e => e.currentTarget.style.background = val === value ? C.accentDim : 'transparent'}
              >
                <div style={{ fontSize: 13, color: val === value ? C.accent : C.text, fontWeight: val === value ? 600 : 400 }}>{label}</div>
                {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
